import { useMemo } from 'react';
import useDebounce from '@/hooks/useDebounce';
import { useGetShippingStatusOptionsQuery } from '@/pages/Business/pages/LogisticOperator/services/shipping-statuses.service';
import { Option } from '@/components/inputs/AutocompleteInput';

const DEBOUNCE_MS = 200;

interface UseTransitControlStatusesAutocompleteProps {
  search?: string;
  minLength?: number;
}

const useTransitControlStatusesAutocomplete = ({
  search,
  minLength = 3,
}: UseTransitControlStatusesAutocompleteProps = {}) => {
  const trimmedLive = (search ?? '').trim();
  const searchTrimmed = useDebounce(trimmedLive, DEBOUNCE_MS);
  const meetsMinimumLength = searchTrimmed.length >= minLength;

  const { data: statusesResponse, isLoading: isShippingStatusesLoading } =
    useGetShippingStatusOptionsQuery({ limit: 1000, offset: 0 });

  const shippingStatusOptions: Option[] = useMemo(() => {
    if (!meetsMinimumLength || !statusesResponse?.items?.length) return [];

    const queryNormalized = searchTrimmed.toLocaleLowerCase('es');
    return statusesResponse.items
      .filter((status) => {
        const commercialName = (status.commercialName ?? '').toLocaleLowerCase('es');
        const internalName = (status.name ?? '').toLocaleLowerCase('es');
        return (
          commercialName.includes(queryNormalized) ||
          internalName.includes(queryNormalized)
        );
      })
      .map((status) => ({
        label: status.commercialName || status.name,
        data: status.id,
      }));
  }, [meetsMinimumLength, searchTrimmed, statusesResponse?.items]);

  return { shippingStatusOptions, isShippingStatusesLoading };
};

export default useTransitControlStatusesAutocomplete;
